import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TREASURY_KIND_CURRENCY, treasuryLocalCodeType } from '@loot/shared';
import {
  api,
  type LegalEntity,
  type TreasuryAccountGroup,
  type TreasuryAccountKind,
  type TreasuryAccounts,
  type TreasuryAccountVersion,
} from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';
import { ListToolbar, SearchBox } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { Modal } from '../../components/Modal.tsx';
import { NetsuiteReferencesSection } from '../../components/NetsuiteReferences.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { ImportButton } from '../masterdata/ImportButton.tsx';
import {
  AbroadFields,
  AccountChoice,
  BankPicker,
  PixFields,
  PixToggle,
  type AbroadValues,
} from '../partners/BankFields.tsx';
import { CommentAction } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const SITUATION_TONE: Record<TreasuryAccountGroup['situation'], Tone> = {
  usable: 'ok',
  out_of_validity: 'neutral',
  awaiting_confirmation: 'accent',
  inactive: 'neutral',
  rejected: 'off',
  superseded: 'neutral',
};
const STATUS_TONE: Record<TreasuryAccountVersion['status'], Tone> = {
  pending: 'accent',
  active: 'ok',
  rejected: 'off',
  superseded: 'neutral',
  inactive: 'neutral',
};
const PLACES = ['br_account', 'intl'] as const;
type Place = (typeof PLACES)[number];
const ACCOUNT_TYPES = ['checking', 'savings', 'payment', 'investment', 'other'] as const;
const PURPOSES = [
  'operating',
  'collection',
  'payments',
  'payroll',
  'investment',
  'tax',
  'fx',
  'cards',
  'other',
] as const;
const CHANNELS = ['manual', 'file', 'api', 'open_finance'] as const;
const SITUATIONS: TreasuryAccountGroup['situation'][] = [
  'usable',
  'awaiting_confirmation',
  'out_of_validity',
  'inactive',
  'rejected',
];
const QUERY_KEY = ['finance', 'bank-accounts'];

/** Resumo da conta sem o número completo (ex.: "Banco 341 · Ag. 0001 · •••• 45-6 · BRL"). */
function useAccountLine() {
  const { t } = useTranslation();
  return (v: TreasuryAccountVersion) =>
    [
      v.bankName,
      v.bankCode && `${t('finance.bankAccounts.fields.bankCode')} ${v.bankCode}`,
      v.branch && `${t('finance.bankAccounts.fields.branch')} ${v.branch}`,
      v.routingNumber && `${t('finance.bankAccounts.fields.routingNumber')} ${v.routingNumber}`,
      v.swiftBic,
      v.localCode &&
        `${t(`finance.bankAccounts.localCodeTypes.${v.localCodeType}`)} ${v.localCode}`,
      v.masked,
      v.currency,
    ]
      .filter(Boolean)
      .join(' · ');
}

function useAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post(`/finance/bank-accounts${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function BankAccountsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [entity, setEntity] = useState('');
  const [situation, setSituation] = useState('');
  const [adding, setAdding] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const line = useAccountLine();
  const data = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<TreasuryAccounts>('/finance/bank-accounts'),
  });
  const legalEntities = useQuery({
    queryKey: ['core', 'legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const { accounts, canAdd } = data.data;
  // Filtro com todas as empresas do grupo (também as que ainda não têm conta) e as das contas.
  const entities = [
    ...new Map([
      ...(legalEntities.data ?? [])
        .filter((e) => e.isActive)
        .map((e) => [e.id, `${e.code} · ${e.legalName}`] as const),
      ...accounts
        .filter((a) => !legalEntities.data?.some((e) => e.id === a.legalEntityId))
        .map((a) => [a.legalEntityId, a.legalEntityCode ?? '—'] as const),
    ]).entries(),
  ];
  const q = search.trim().toLowerCase();
  const rows = accounts.filter(
    (a) =>
      (!entity || a.legalEntityId === entity) &&
      (!situation || a.situation === situation) &&
      (!q ||
        `${a.latest.nickname} ${line(a.latest)} ${a.legalEntityCode ?? ''}`
          .toLowerCase()
          .includes(q)),
  );
  const open = accounts.find((a) => a.accountKey === openKey) ?? null;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.companyBankAccounts')}
        description={t('finance.bankAccounts.about')}
        actions={
          canAdd && <Button onClick={() => setAdding(true)}>{t('finance.bankAccounts.add')}</Button>
        }
      />
      <ListToolbar action={<ImportButton kind="company_bank_accounts" />}>
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder={t('finance.bankAccounts.search')}
        />
        <Select
          id="bank-filter-entity"
          aria-label={t('finance.bankAccounts.fields.legalEntity')}
          value={entity}
          onChange={(e) => setEntity(e.target.value)}
        >
          <option value="">{t('finance.bankAccounts.allEntities')}</option>
          {entities.map(([id, code]) => (
            <option key={id} value={id}>
              {code}
            </option>
          ))}
        </Select>
        <Select
          id="bank-filter-situation"
          aria-label={t('finance.bankAccounts.fields.situation')}
          value={situation}
          onChange={(e) => setSituation(e.target.value)}
        >
          <option value="">{t('finance.bankAccounts.allSituations')}</option>
          {SITUATIONS.map((s) => (
            <option key={s} value={s}>
              {t(`finance.bankAccounts.situations.${s}`)}
            </option>
          ))}
        </Select>
      </ListToolbar>
      <Table
        head={[
          t('finance.bankAccounts.fields.legalEntity'),
          t('finance.bankAccounts.fields.nickname'),
          t('finance.bankAccounts.fields.account'),
          t('finance.bankAccounts.fields.purpose'),
          t('finance.bankAccounts.fields.situation'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={6}>{t('finance.bankAccounts.empty')}</EmptyRow>}
        {rows.map((a) => (
          <tr key={a.accountKey}>
            <td className="font-mono text-sm">{a.legalEntityCode ?? '—'}</td>
            <td>{a.latest.nickname}</td>
            <td className="font-mono text-xs">{line(a.current ?? a.latest)}</td>
            <td className="text-sm">{t(`finance.bankAccounts.purposes.${a.latest.purpose}`)}</td>
            <td>
              <Badge tone={SITUATION_TONE[a.situation]}>
                {t(`finance.bankAccounts.situations.${a.situation}`)}
              </Badge>
            </td>
            <td className="text-right">
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setOpenKey(a.accountKey)}>
                  {t('finance.bankAccounts.open')}
                </Button>
                <MasterDeleteButton table="treasury.bank_account" id={a.latest.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {open && <AccountDetail a={open} onClose={() => setOpenKey(null)} />}
      {adding && <AccountForm onDone={() => setAdding(false)} />}
    </div>
  );
}

function AccountDetail({ a, onClose }: { a: TreasuryAccountGroup; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const line = useAccountLine();
  const [changing, setChanging] = useState(false);
  const main = a.current ?? a.latest;
  const item = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
  return (
    <Modal
      open
      size="xl"
      title={main.nickname}
      description={`${a.legalEntityCode ?? ''} · ${a.legalEntityName ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <MasterDeleteButton table="treasury.bank_account" id={main.id} />
          {a.canChange && (
            <Button variant="secondary" onClick={() => setChanging(true)}>
              {t('finance.bankAccounts.change')}
            </Button>
          )}
          <Button onClick={onClose}>{t('finance.bankAccounts.close')}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-sm">{line(main)}</span>
          <Badge tone={SITUATION_TONE[a.situation]}>
            {t(`finance.bankAccounts.situations.${a.situation}`)}
          </Badge>
        </div>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          {item(
            t('finance.bankAccounts.fields.kind'),
            t(`finance.bankAccounts.kinds.${main.kind}`),
          )}
          {item(t('finance.bankAccounts.fields.country'), main.country)}
          {item(t('finance.bankAccounts.fields.currency'), main.currency)}
          {item(
            t('finance.bankAccounts.fields.purpose'),
            t(`finance.bankAccounts.purposes.${main.purpose}`),
          )}
          {item(
            t('finance.bankAccounts.fields.validity'),
            `${fmt.date(main.validFrom)} → ${main.validUntil ? fmt.date(main.validUntil) : '—'}`,
          )}
          {item(
            t('finance.bankAccounts.fields.integrationChannel'),
            t(`finance.bankAccounts.channels.${main.integrationChannel}`),
          )}
          {item(t('finance.bankAccounts.fields.ispb'), main.ispb)}
          {main.kind !== 'br_account' &&
            item(t('finance.bankAccounts.fields.bankAddress'), main.bankAddress)}
          {main.intermediarySwift &&
            item(
              t('finance.bankAccounts.intermediaryTitle'),
              [main.intermediarySwift, main.intermediaryName, main.intermediaryAccount]
                .filter(Boolean)
                .join(' · '),
            )}
          {item(
            t('finance.bankAccounts.fields.pixKey'),
            [main.pixKeyType && t(`partners.bank.pixTypes.${main.pixKeyType}`), main.pixKey]
              .filter(Boolean)
              .join(' · '),
          )}
          {item(t('finance.bankAccounts.fields.responsible'), main.responsibleName)}
        </dl>
        {a.current && <VersionActions v={a.current} />}
        {a.pending && (
          <div className="flex flex-col gap-2 rounded-md border border-rule bg-paper p-3">
            <p className="text-sm font-medium">
              {t('finance.bankAccounts.pendingTitle', { version: a.pending.version })}
            </p>
            {a.current && <p className="font-mono text-sm">{line(a.pending)}</p>}
            <p className="text-sm text-muted">
              {t('finance.bankAccounts.requestedBy', {
                who: a.pending.createdByName ?? '—',
                reason: a.pending.changeReason,
              })}
            </p>
            <VersionActions v={a.pending} />
          </div>
        )}
        <VersionHistory versions={a.versions} />
        <NetsuiteReferencesSection entityType="treasury_bank_account" entityId={a.accountKey} />
      </div>
      {changing && (
        <AccountForm
          accountKey={a.accountKey}
          entityId={a.legalEntityId}
          base={main}
          onDone={() => setChanging(false)}
        />
      )}
    </Modal>
  );
}

function VersionActions({ v }: { v: TreasuryAccountVersion }) {
  const { t } = useTranslation();
  const action = useAction();
  const [revealed, setRevealed] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: (purpose: string) =>
      api.post<Record<string, string>>(`/finance/bank-accounts/versions/${v.id}/reveal`, {
        purpose,
      }),
    onSuccess: (res) => setRevealed(Object.values(res)[0] ?? null),
  });
  const path = (verb: string) => `/versions/${v.id}/${verb}`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-start gap-2">
        {v.actions.decide && (
          <>
            <CommentAction
              label={t('finance.bankAccounts.confirm')}
              fieldLabel={t('finance.bankAccounts.fields.comment')}
              variant="primary"
              pending={action.isPending}
              error={action.error}
              onConfirm={(comment) => action.mutate({ path: path('confirm'), body: { comment } })}
            />
            <CommentAction
              label={t('finance.bankAccounts.reject')}
              fieldLabel={t('finance.bankAccounts.fields.reason')}
              variant="danger"
              pending={action.isPending}
              error={action.error}
              onConfirm={(comment) => action.mutate({ path: path('reject'), body: { comment } })}
            />
          </>
        )}
        {v.actions.inactivate && (
          <CommentAction
            label={t('finance.bankAccounts.inactivate')}
            fieldLabel={t('finance.bankAccounts.fields.reason')}
            pending={action.isPending}
            error={action.error}
            onConfirm={(reason) => action.mutate({ path: path('inactivate'), body: { reason } })}
          />
        )}
        {v.actions.reveal &&
          (revealed ? (
            <span className="rounded-md border border-rule px-3 py-2 font-mono text-sm">
              {revealed}
            </span>
          ) : (
            <CommentAction
              label={t('finance.bankAccounts.reveal')}
              fieldLabel={t('finance.bankAccounts.revealPurpose')}
              pending={reveal.isPending}
              error={reveal.error}
              onConfirm={(purpose) => reveal.mutate(purpose)}
            />
          ))}
      </div>
      {v.actions.decideBlockedBySeparation && (
        <p className="text-xs text-muted">{t('finance.bankAccounts.separation')}</p>
      )}
      <ErrorBox error={action.error ?? reveal.error} />
    </div>
  );
}

function VersionHistory({ versions }: { versions: TreasuryAccountVersion[] }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const line = useAccountLine();
  return (
    <Table
      head={[
        t('finance.bankAccounts.fields.version'),
        t('finance.bankAccounts.fields.situation'),
        t('finance.bankAccounts.fields.account'),
        t('finance.bankAccounts.fields.requested'),
        t('finance.bankAccounts.fields.decision'),
      ]}
    >
      {versions.map((v) => (
        <tr key={v.id}>
          <td>{v.version}</td>
          <td>
            <Badge tone={STATUS_TONE[v.status]}>
              {t(`finance.bankAccounts.statuses.${v.status}`)}
            </Badge>
          </td>
          <td className="font-mono text-xs">
            {v.nickname} · {line(v)}
          </td>
          <td className="text-xs">
            {v.createdByName ?? '—'} · {fmt.dateTime(v.createdAt)}
            <div className="text-muted">{v.changeReason}</div>
          </td>
          <td className="text-xs">
            {v.decidedAt ? `${v.decidedByName ?? '—'} · ${fmt.dateTime(v.decidedAt)}` : '—'}
            {v.decisionComment && <div className="text-muted">{v.decisionComment}</div>}
            {v.inactivationReason && <div className="text-muted">{v.inactivationReason}</div>}
          </td>
        </tr>
      ))}
    </Table>
  );
}

function AccountForm({
  accountKey,
  entityId,
  base,
  onDone,
}: {
  accountKey?: string;
  entityId?: string;
  base?: TreasuryAccountVersion;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const action = useAction();
  const entities = useQuery({
    queryKey: ['core', 'legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
    enabled: !accountKey,
  });
  const [place, setPlace] = useState<Place>(
    base && base.kind !== 'br_account' ? 'intl' : 'br_account',
  );
  const [start] = useState(() => ({
    legalEntityId: '',
    nickname: base?.nickname ?? '',
    currency: base?.currency ?? 'BRL',
    bankName: base?.bankName ?? '',
    bankCode: base?.bankCode ?? '',
    ispb: base?.ispb ?? '',
    branch: base?.branch ?? '',
    accountNumber: '',
    accountType: base?.accountType ?? 'checking',
    iban: '',
    swiftBic: base?.swiftBic ?? '',
    country: !base || base.country === 'BR' ? 'US' : base.country,
    intlFormat: base?.kind === 'iban' ? 'iban' : 'account',
    withPix: base?.pixKey ? 'yes' : '',
    pixKeyType: base?.pixKeyType ?? 'cnpj',
    pixKey: base?.pixKey ?? '',
    bankAddress: base?.bankAddress ?? '',
    // Nos EUA, o código local é o routing number.
    localCode: base?.localCode ?? base?.routingNumber ?? '',
    withIntermediary: base?.intermediarySwift ? 'yes' : '',
    intermediarySwift: base?.intermediarySwift ?? '',
    intermediaryName: base?.intermediaryName ?? '',
    intermediaryAccount: base?.intermediaryAccount ?? '',
    purpose: base?.purpose ?? 'operating',
    validFrom: base?.validFrom ?? new Date().toISOString().slice(0, 10),
    validUntil: base?.validUntil ?? '',
    integrationChannel: base?.integrationChannel ?? 'manual',
    integrationNotes: base?.integrationNotes ?? '',
    changeReason: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const kindOf = (p: Place, s: typeof f): TreasuryAccountKind => {
    if (p === 'br_account') return 'br_account';
    if (s.intlFormat === 'iban') return 'iban';
    return s.country === 'US' ? 'us_account' : 'foreign_account';
  };
  const kind = kindOf(place, f);
  // Mudar o tipo da conta troca a moeda sugerida; a que a pessoa escolheu continua.
  const change = (nextPlace: Place, patch: Partial<AbroadValues>) =>
    setF((s) => {
      const next = { ...s, ...patch };
      return s.currency === TREASURY_KIND_CURRENCY[kindOf(place, s)]
        ? { ...next, currency: TREASURY_KIND_CURRENCY[kindOf(nextPlace, next)] }
        : next;
    });
  const changePlace = (next: Place) => {
    change(next, {});
    setPlace(next);
  };
  const legalEntityId = entityId ?? f.legalEntityId;
  const common = {
    kind,
    legalEntityId,
    nickname: f.nickname,
    currency: f.currency,
    bankName: f.bankName,
    purpose: f.purpose,
    validFrom: f.validFrom,
    validUntil: f.validUntil || null,
    integrationChannel: f.integrationChannel,
    integrationNotes: f.integrationNotes,
    changeReason: f.changeReason,
  };
  // Conta fora do Brasil: endereço do banco e, se houver, o banco intermediário.
  const abroad = {
    bankAddress: f.bankAddress,
    intermediary: f.withIntermediary
      ? {
          swiftBic: f.intermediarySwift,
          name: f.intermediaryName,
          account: f.intermediaryAccount || null,
        }
      : null,
  };
  const localCodeType = kind === 'foreign_account' ? treasuryLocalCodeType(f.country) : null;
  const bodies: Record<TreasuryAccountKind, object> = {
    br_account: {
      ...common,
      bankCode: f.bankCode,
      ispb: f.ispb || null,
      branch: f.branch,
      accountNumber: f.accountNumber,
      accountType: f.accountType,
      // PIX só existe em conta no Brasil: trocar o tipo da conta não deixa a chave para trás.
      pixKeyType: f.withPix ? f.pixKeyType : null,
      pixKey: f.withPix ? f.pixKey : null,
    },
    iban: { ...common, ...abroad, iban: f.iban, swiftBic: f.swiftBic || null },
    us_account: {
      ...common,
      ...abroad,
      routingNumber: f.localCode.replace(/[\s.-]+/g, ''),
      accountNumber: f.accountNumber,
      accountType: f.accountType,
      swiftBic: f.swiftBic || null,
    },
    foreign_account: {
      ...common,
      ...abroad,
      country: f.country,
      swiftBic: f.swiftBic,
      accountNumber: f.accountNumber,
      accountType: f.accountType,
      localCode: localCodeType ? f.localCode : null,
    },
  };
  const text = (k: keyof typeof f, required = true) => (
    <Field label={t(`finance.bankAccounts.fields.${k}`)} required={required}>
      <Input id={`tba-${k}`} required={required} value={f[k]} onChange={set(k)} />
    </Field>
  );
  const choice = (k: keyof typeof f, options: readonly string[], group: string) => (
    <Field label={t(`finance.bankAccounts.fields.${k}`)} required>
      <Select id={`tba-${k}`} value={f[k]} onChange={set(k)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {t(`finance.bankAccounts.${group}.${o}`)}
          </option>
        ))}
      </Select>
    </Field>
  );
  return (
    <FormModal
      open
      size="xl"
      title={accountKey ? t('finance.bankAccounts.change') : t('finance.bankAccounts.add')}
      description={t('finance.bankAccounts.formHint')}
      dirty={
        place !== (base && base.kind !== 'br_account' ? 'intl' : 'br_account') || changed(start, f)
      }
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.bankAccounts.send')}
      onSave={() =>
        action.mutate(
          {
            path: accountKey ? `/${accountKey}/versions` : '',
            body: bodies[kind],
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        {!accountKey && (
          <Field label={t('finance.bankAccounts.fields.legalEntity')} required>
            <Select
              id="tba-legalEntityId"
              required
              value={f.legalEntityId}
              onChange={set('legalEntityId')}
            >
              <option value="">{t('finance.bankAccounts.chooseEntity')}</option>
              {(entities.data ?? [])
                .filter((e) => e.isActive)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} · {e.legalName}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        <AccountChoice value={place} options={PLACES} onChange={changePlace} />
        {text('nickname')}
        <Field label={t('finance.bankAccounts.fields.currency')} required>
          <CurrencySelect
            id="tba-currency"
            value={f.currency}
            onChange={(currency) => setF((s) => ({ ...s, currency }))}
          />
        </Field>
        {place === 'br_account' && (
          <>
            <BankPicker
              value={{ bankIspb: f.ispb, bankCode: f.bankCode, bankName: f.bankName }}
              onChange={(v) =>
                setF((s) => ({
                  ...s,
                  ispb: v.bankIspb,
                  bankCode: v.bankCode,
                  bankName: v.bankName,
                }))
              }
            />
            {text('branch')}
            {text('accountNumber')}
            {choice('accountType', ACCOUNT_TYPES, 'accountTypes')}
            <PixToggle
              checked={!!f.withPix}
              onChange={(on) => setF((s) => ({ ...s, withPix: on ? 'yes' : '' }))}
            />
            {f.withPix && (
              <PixFields
                type={f.pixKeyType}
                value={f.pixKey}
                onChange={(patch) => setF((s) => ({ ...s, ...patch }))}
              />
            )}
          </>
        )}
        {place === 'intl' && (
          <>
            <AbroadFields
              f={f}
              onChange={(patch) => change(place, patch)}
              swiftRequired={kind === 'foreign_account'}
              addressRequired={false}
            />
            {f.intlFormat === 'account' && choice('accountType', ACCOUNT_TYPES, 'accountTypes')}
          </>
        )}
        {choice('purpose', PURPOSES, 'purposes')}
        <Field label={t('finance.bankAccounts.fields.validFrom')} required>
          <Input
            id="tba-validFrom"
            type="date"
            required
            value={f.validFrom}
            onChange={set('validFrom')}
          />
        </Field>
        <Field label={t('finance.bankAccounts.fields.validUntil')}>
          <Input
            id="tba-validUntil"
            type="date"
            value={f.validUntil}
            onChange={set('validUntil')}
          />
        </Field>
        {choice('integrationChannel', CHANNELS, 'channels')}
        {text('integrationNotes', false)}
        <Field
          label={t('finance.bankAccounts.fields.changeReason')}
          className="sm:col-span-2"
          required
        >
          <Textarea
            id="tba-changeReason"
            required
            minLength={3}
            value={f.changeReason}
            onChange={set('changeReason')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
